import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { AttendanceDayRegister } from "@/components/worker/AttendanceDayRegister";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { AlertTriangle, ChevronRight, Users, Download, Trash2 } from "lucide-react";

const SERVICES = [
  { key: "pre_scuola", label: "Pre" },
  { key: "post_scuola", label: "Post" },
  { key: "centro_estivo", label: "Centro estivo" },
  { key: "blue_box", label: "Blue Box" },
];
const CTX_TYPE = { pre_scuola: "operational_site", post_scuola: "operational_site", centro_estivo: "group", blue_box: "blue_box" };

function fmtDate(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long" }); }
  catch { return iso; }
}

function MonthlyGrid({ appaltoId, service, ctxType, ctxId, year, month, onEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ appalto_id: appaltoId, service, context_type: ctxType, year, month });
      if (ctxId) q.set("context_id", ctxId);
      const r = await api.get(`/office/attendance/monthly?${q.toString()}`);
      setData(r.data);
      const ids = new Set((r.data.participants || []).map((p) => p.id));
      setSel((v) => v.filter((id) => ids.has(id)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setData(null); }
    finally { setLoading(false); }
  }, [appaltoId, service, ctxType, ctxId, year, month]);
  useEffect(() => { load(); }, [load]);
  const doDelete = async () => {
    setDeleting(true);
    try {
      const r = await api.post("/office/attendance/participants/delete", {
        appalto_id: appaltoId, service, context_type: ctxType, context_id: ctxId || null, participant_ids: sel });
      toast.success(`${r.data.deleted} ${r.data.deleted === 1 ? "riga eliminata" : "righe eliminate"}`);
      setSel([]); setConfirm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setConfirm(false); load(); }
    finally { setDeleting(false); }
  };

  if (loading) return <p className="text-sm text-slate-400 py-4 text-center">Caricamento…</p>;
  if (!data) return null;
  const visibleIds = data.participants.map((p) => p.id);
  const allOn = visibleIds.length > 0 && visibleIds.every((id) => sel.includes(id));
  const toggleRow = (id) => setSel((v) => v.includes(id) ? v.filter((x) => x !== id) : [...v, id]);
  const toggleAllRows = () => setSel(allOn ? [] : visibleIds);
  if (!data.participants.length) return <p className="text-sm text-slate-400 py-4 text-center" data-testid="monthly-empty">Nessun utente Presenze per questo contesto.</p>;
  const cellCls = (v) => v === "P" ? "bg-emerald-500 text-white border-emerald-600"
    : v === "A" ? "bg-red-500 text-white border-red-600"
    : v === "na" ? "bg-slate-50 border-slate-100 text-transparent"
    : "bg-amber-50/50 border-slate-200 text-slate-300";
  return (
    <div>
    <div className="overflow-x-auto border border-slate-200 rounded-xl" data-testid="monthly-grid">
      <table className="border-collapse text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-slate-100 text-left px-3 py-2 min-w-[180px] border-b border-r border-slate-200 font-semibold text-slate-600">
              <div className="flex items-center gap-2">
                <Checkbox checked={allOn} onCheckedChange={toggleAllRows}
                  aria-label="Seleziona tutte le righe" data-testid="pu-select-all" />
                Utente
              </div>
            </th>
            {data.days.map((d) => (
              <th key={d.day} className={`px-0 py-1 w-8 border-b border-slate-200 text-center ${d.weekend ? "bg-slate-100" : "bg-white"}`}>
                <div className="text-[9px] text-slate-400 leading-none">{d.dow}</div>
                <div className="text-[11px] font-semibold text-slate-600 leading-tight">{d.day}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.participants.map((p) => (
            <tr key={p.id} data-testid={`monthly-row-${p.id}`}>
              <td className={`sticky left-0 z-10 px-3 py-1.5 border-b border-r border-slate-200 font-medium text-slate-800 whitespace-nowrap ${sel.includes(p.id) ? "bg-red-50" : "bg-white"}`}>
                <div className="flex items-center gap-2">
                  <Checkbox checked={sel.includes(p.id)} onCheckedChange={() => toggleRow(p.id)}
                    aria-label={`Seleziona ${p.cognome} ${p.nome}`} data-testid={`pu-select-row-${p.id}`} />
                  {p.cognome} {p.nome}
                </div>
              </td>
              {data.days.map((d) => {
                const v = p.cells[String(d.day)]; const editable = v !== "na";
                return (
                  <td key={d.day} className="p-0.5 border-b border-slate-100 text-center">
                    <button type="button" disabled={!editable}
                      onClick={() => editable && onEdit(`${data.year}-${String(data.month).padStart(2,"0")}-${String(d.day).padStart(2,"0")}`)}
                      data-testid={`monthly-cell-${p.id}-${d.day}`}
                      className={`h-7 w-7 rounded border text-[11px] font-bold ${cellCls(v)} ${editable ? "hover:opacity-80 cursor-pointer" : "cursor-default"}`}>
                      {v === "na" ? "" : (v || "·")}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="flex items-center justify-end gap-3 mt-2">
      {sel.length > 0 && <span className="text-xs text-slate-500" data-testid="pu-selected-count">{sel.length} {sel.length === 1 ? "riga selezionata" : "righe selezionate"}</span>}
      <Button type="button" variant="outline" disabled={!sel.length || deleting} onClick={() => setConfirm(true)}
        data-testid="pu-delete-selected"
        className="rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
        <Trash2 className="h-4 w-4 mr-1.5" /> Elimina righe selezionate
      </Button>
    </div>
    <AlertDialog open={confirm} onOpenChange={(o) => !deleting && setConfirm(o)}>
      <AlertDialogContent data-testid="pu-delete-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
          <AlertDialogDescription>
            Stai per eliminare {sel.length} {sel.length === 1 ? "riga" : "righe"} delle Presenze utenti.
            Le registrazioni selezionate non saranno più disponibili nell'account Lavoratore, nella tabella Presenze utenti e nelle successive esportazioni Excel.
            Vuoi procedere?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} data-testid="pu-delete-cancel">Annulla</AlertDialogCancel>
          <Button type="button" onClick={doDelete} disabled={deleting} data-testid="pu-delete-confirm"
            className="rounded-lg bg-red-600 hover:bg-red-700 text-white">
            {deleting ? "Eliminazione…" : "Conferma eliminazione"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </div>
  );
}

function AnomaliesList({ appaltoId, service, year, month, onOpen }) {
  const [items, setItems] = useState(null);
  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams({ appalto_id: appaltoId, service, year, month });
      const r = await api.get(`/office/attendance/pending-registers?${q.toString()}`);
      setItems(r.data.items || []);
    } catch { setItems([]); }
  }, [appaltoId, service, year, month]);
  useEffect(() => { load(); }, [load]);
  if (items === null) return <p className="text-sm text-slate-400 py-6 text-center">Caricamento…</p>;
  if (!items.length) return <p className="text-sm text-slate-400 py-6 text-center" data-testid="anomalies-empty">Nessun registro problematico nel perimetro selezionato.</p>;
  return (
    <div className="space-y-2" data-testid="anomalies-list">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid={`anomaly-${i}`}>
          <div className="flex items-start gap-2.5">
            <span className="h-8 w-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><AlertTriangle className="h-4 w-4" /></span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 capitalize">{fmtDate(it.date)} · {SERVICES.find((s) => s.key === it.service)?.label || it.service}{it.context_label ? ` · ${it.context_label}` : ""}</p>
              <p className="text-xs text-slate-600">{it.status === "da_compilare" ? "Registro non compilato" : "Registro incompleto"} · {it.compiled}/{it.expected} utenti compilati</p>
              <p className="text-[11px] text-slate-500 mt-0.5 truncate">Operatori: {(it.workers || []).map((w) => w.name).join(", ") || "—"}</p>
              <button data-testid={`anomaly-open-${i}`} onClick={() => onOpen(it)}
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1.5 bg-[#22364D] text-white hover:bg-[#2f4a6b]">
                Apri registro <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function PresenzeUtentiExport({ appalti, appaltoId, month, year }) {
  const [service, setService] = useState("");
  const [contexts, setContexts] = useState([]);
  const [ctxIds, setCtxIds] = useState([]);
  const [tab, setTab] = useState("presenze");
  const [anomCount, setAnomCount] = useState(0);
  const [dayReg, setDayReg] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const appalto = (appalti || []).find((a) => a.id === appaltoId);
  const availServices = useMemo(() => SERVICES.filter((sv) =>
    (appalto?.servizi || []).some((s) => s.slug === sv.key && s.stato === "attivo")), [appalto]);
  const ctxType = service ? CTX_TYPE[service] : "";
  const needsCtx = ctxType === "operational_site" || ctxType === "group";
  const ready = appaltoId && service && (!needsCtx || ctxIds.length > 0);
  const labelOf = (id) => contexts.find((c) => c.context_id === id)?.label || "";

  // L'appalto proviene dal contesto generale "Esportazione dati": al suo cambio azzero servizio/contesti locali.
  useEffect(() => { setService(""); setCtxIds([]); setContexts([]); }, [appaltoId]);

  useEffect(() => { setCtxIds([]); setContexts([]);
    if (appaltoId && service && needsCtx) {
      api.get(`/office/attendance/contexts?appalto_id=${appaltoId}&service=${service}`).then((r) => setContexts(r.data.items || [])).catch(() => setContexts([]));
    }
  }, [appaltoId, service, needsCtx]);

  useEffect(() => {
    if (!appaltoId || !service) { setAnomCount(0); return; }
    const q = new URLSearchParams({ appalto_id: appaltoId, service, year, month });
    api.get(`/office/attendance/pending-registers?${q.toString()}`).then((r) => setAnomCount(r.data.count || 0)).catch(() => setAnomCount(0));
  }, [appaltoId, service, year, month, refreshKey]);

  const toggleCtx = (id) => setCtxIds((v) => v.includes(id) ? v.filter((x) => x !== id) : [...v, id]);
  const allSelected = needsCtx && contexts.length > 0 && ctxIds.length === contexts.length;
  const toggleAll = () => setCtxIds(allSelected ? [] : contexts.map((c) => c.context_id));

  const gridsCtx = needsCtx ? ctxIds.map((id) => ({ id, label: labelOf(id) })) : [{ id: null, label: "Blue Box" }];

  const doExport = async () => {
    setExporting(true);
    try {
      const q = new URLSearchParams({ appalto_id: appaltoId, service, year, month });
      if (needsCtx) q.set("context_ids", ctxIds.join(","));
      const r = await api.get(`/office/attendance/monthly/export?${q.toString()}`, { responseType: "blob" });
      const dispo = r.headers["content-disposition"] || "";
      const m = dispo.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `Presenze_${service}_${month}_${year}.xlsx`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Excel generato: ${fname}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  return (
    <div className="w-full" data-testid="presenze-utenti-export">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 mb-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2"><Users className="h-4 w-4" /> Presenze utenti · {appalto ? `${appalto.nome} · ` : ""}{month}/{year}</p>
        {!appaltoId ? (
          <p className="text-sm text-amber-600" data-testid="pu-no-appalto">Seleziona un Appalto nel contesto «Esportazione dati» per gestire le Presenze utenti.</p>
        ) : (
        <div className="mb-1">
          <div className="mb-3">
            <Label className="text-xs text-slate-500">Servizio</Label>
            <Select value={service} onValueChange={setService}>
              <SelectTrigger className="rounded-lg" data-testid="pu-service"><SelectValue placeholder="Scegli servizio" /></SelectTrigger>
              <SelectContent>{availServices.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        {needsCtx && contexts.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs text-slate-500">{ctxType === "group" ? "Gruppi" : "Sedi operative"} (selezione multipla)</Label>
              <button type="button" onClick={toggleAll} data-testid="pu-toggle-all"
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${allSelected ? "bg-[#22364D] text-white border-[#22364D]" : "bg-white text-slate-600 border-slate-300"}`}>
                {allSelected ? "Deseleziona tutti" : "Tutti"}
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="pu-context-multi">
              {contexts.map((c) => {
                const on = ctxIds.includes(c.context_id);
                return (
                  <button key={c.context_id} type="button" onClick={() => toggleCtx(c.context_id)}
                    data-testid={`pu-ctx-${c.context_id}`}
                    className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${on ? "bg-emerald-500 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        </div>
        )}
      </div>

      {appaltoId && (!ready ? (
        <p className="text-sm text-amber-600" data-testid="pu-hint">Seleziona il servizio {needsCtx ? (ctxType === "group" ? "e almeno un gruppo" : "e almeno una sede") : ""} per visualizzare le presenze.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button data-testid="pu-tab-presenze" onClick={() => setTab("presenze")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "presenze" ? "bg-[#22364D] text-white" : "bg-slate-100 text-slate-600"}`}>Presenze</button>
            <button data-testid="pu-tab-anomalie" onClick={() => setTab("anomalie")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 ${tab === "anomalie" ? "bg-[#22364D] text-white" : "bg-slate-100 text-slate-600"}`}>
              Non inserite correttamente
              {anomCount > 0 && <span className="min-w-[20px] h-5 px-1 rounded-full bg-amber-500 text-white text-[11px] font-bold inline-flex items-center justify-center" data-testid="pu-anom-badge">{anomCount}</span>}
            </button>
            <div className="flex-1" />
            <Button type="button" onClick={doExport} disabled={exporting} data-testid="pu-export-excel"
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
              <Download className="h-4 w-4 mr-1.5" /> {exporting ? "Esporto…" : "Esporta Excel"}
            </Button>
          </div>

          {tab === "presenze" ? (
            <div className="space-y-5" data-testid="pu-grids">
              {gridsCtx.map((g) => (
                <div key={g.id || "bluebox"}>
                  <p className="text-sm font-semibold text-slate-700 mb-2" data-testid={`pu-grid-title-${g.id || "bluebox"}`}>{g.label}</p>
                  <MonthlyGrid key={`${g.id}-${refreshKey}`} appaltoId={appaltoId} service={service} ctxType={ctxType} ctxId={g.id || ""} year={year} month={month}
                    onEdit={(d) => setDayReg({ date: d, ctx: { service, type: ctxType, context_id: g.id || null, appalto_id: appaltoId, label: g.label } })} />
                </div>
              ))}
            </div>
          ) : (
            <AnomaliesList key={refreshKey} appaltoId={appaltoId} service={service} year={year} month={month}
              onOpen={(it) => setDayReg({ date: it.date, ctx: { service: it.service, type: it.context_type, context_id: it.context_id, appalto_id: it.appalto_id, label: it.context_label } })} />
          )}
        </>
      ))}

      {dayReg && (
        <AttendanceDayRegister open onClose={() => { setDayReg(null); setRefreshKey((k) => k + 1); }}
          ctx={dayReg.ctx} date={dayReg.date} serviceLabel={SERVICES.find((s) => s.key === dayReg.ctx.service)?.label || dayReg.ctx.service} />
      )}
    </div>
  );
}

export default PresenzeUtentiExport;
